// noinspection DirectConsoleLog
// this file IS the logger; direct console usage is intentional.
import { captureException, captureMessage } from '@sentry/react'

import { appDebugService } from '@/services/appDebug.service'

class Logger {
  private shouldLog(): boolean {
    return appDebugService.isActive()
  }

  /**
   * Logs a message to the console in non-production environments.
   * To see such logs in production, the {@link shouldLog} function should return true.
   * @param args vararg list of arguments to log
   */
  info(...args: unknown[]): void {
    if (!this.shouldLog()) return

    console.info(...args)
  }

  /**
   * Always logs an error to the console. In production, also sends the error to Sentry.
   * @param message custom error message to include in the log
   * @param error exception or error object
   * @param sendToSentry whether to send the error to Sentry, defaults to false
   */
  warn(message: string, error?: unknown, sendToSentry: boolean = false): void {
    console.warn(message, error)

    if (sendToSentry) {
      this.sendToSentry(message, error)
    }
  }

  /**
   * Always logs an error to the console. In production, also sends the error to Sentry.
   * @param message custom error message to include in the log
   * @param error exception or error object
   * @param sendToSentry whether to send the error to Sentry, defaults to true
   */
  error(message: string, error?: unknown, sendToSentry: boolean = true): void {
    console.error(message, error)

    if (sendToSentry) {
      this.sendToSentry(message, error)
    }
  }

  private sendToSentry(message: string, error?: unknown): void {
    if (error instanceof Error) {
      captureException(error, { extra: { message } }) // Creates an issue based on the exception
    } else if (error !== undefined) {
      captureMessage(message, { extra: { error } }) // Creates an issue with a message in the title
    } else {
      captureMessage(message)
    }
  }
}

export const logger = new Logger()
